/* eslint-disable indent */
import * as React from 'react';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import {
  MenuItemNames,
  ConnectorActions,
  ConnectorInfo,
  ConnectorShortNames,
  oauthDialogConnectors,
  dialogConnectors
} from 'types/types';
import { useMessages } from 'modules/intl/LocaleIntlProvider';
import { OauthPopupLoginAndRedirect } from 'modules/oauth/oauthPopupLoginAndRedirect';
import { postRequest } from 'utils/helper';
import { useUIContext } from 'modules/common/hooks/useUIContext';
import { ErrorPage } from 'modules/common/routes/ErrorPage';
import { NOT_USED_CONNECTORS } from './constants';
import { MenuPopper } from './MenuPopper';
import { ReactComponent as QbOnlineButton } from './Images/QBOnline_default_green_button.svg';
import { BamboohrInstallPage } from './IntermediateInstallPage/BambooHrInstallPage';
import { RepliconConnectorInstallPage } from './IntermediateInstallPage/RepliconConnectorInstallPage';
import { ComputerEaseInstallPage } from './IntermediateInstallPage/ComputerEaseInstallPage';
import { JiraInstallDialogPage } from './IntermediateInstallPage/JiraInstallDialogPage';
import { ZendeskInstallDialogPage } from './IntermediateInstallPage/ZendeskInstallDialogPage';
import { RequestConnectionDialog } from './IntermediateInstallPage/RequestConnectionDialog';
import { ToastSnackBar } from './Setting/ToastSnackBar';

const useStyles = makeStyles((theme) => ({
  infoView: {
    width: theme.spacing(25)
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    flexDirection: 'column'
  },
  connectorIcon: {
    alignSelf: 'center',
    display: 'flex',
    height: theme.spacing(15),
    width: theme.spacing(15),
    borderRadius: theme.spacing(3),
    padding: theme.spacing(2),
    border: `solid 3px ${theme.palette.grey[200]}`
  },
  defaultInstallButton: {
    width: theme.spacing(15),
    marginBottom: theme.spacing(2)
  },
  connectorInstallButton: {
    marginBottom: theme.spacing(1.5)
  },
  divider: {
    borderBottomWidth: '1px',
    width: theme.spacing(25),
    color: theme.palette.grey[200]
  },
  connected: {
    display: 'flex',
    margin: theme.spacing(-1.5, 0, 0, 0),
    ...theme.typography.caption,
    fontSize: theme.spacing(1.5),
    color: theme.palette.common.white,
    backgroundColor: theme.palette.success.dark,
    width: 'fit-content',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0, 0.8)
  },
  imageConnectedDiv: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: theme.spacing(2)
  },
  textDiv: {
    margin: theme.spacing(0, 1, 2, 4),
    color: theme.palette.text.primary
  },
  heading: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body2.fontSize
  },
  scope: {
    fontSize: theme.typography.body2.fontSize
  },
  nextline: {
    whiteSpace: 'pre'
  },
  marginBottom: {
    marginBottom: theme.spacing(1.5)
  }
}));

type ConnectorDetailSidePanelProps = {
  info: ConnectorInfo;
  isInstalled: boolean;
  handleDialogOpen: () => void;
};

const messageKeys = ['connectors.uninstall', 'connectorSetting.install'];

export const ConnectorDetailSidePanel: React.FC<ConnectorDetailSidePanelProps> = ({
  info,
  isInstalled,
  handleDialogOpen
}: ConnectorDetailSidePanelProps) => {
  const menuPopperMessages = useMessages(messageKeys);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [operation, setOperation] = useState('install');
  const [addConnectionFeature, setAddConnectionFeature] = useState(false);
  const [isCustomConnection, setIsCustomConnection] = useState(false);
  const [installdialogOpen, setInstallDialogOpen] = useState({
    bamboohr: false,
    computerease: false,
    replicon: false,
    oauthdialog: false
  });
  const [jiraDialog, setJiraDialog] = useState({
    jira: {
      name: 'Basic Auth',
      open: false
    }
  });
  const [requestDialog, setRequestDialog] = useState(false);
  const [showErrorPage, setShowErrorPage] = useState<boolean>(false);
  const [isSignInAlertOpen, setIsSignInAlertOpen] = useState(false);

  const { genericKeyValues } = useUIContext() || {};
  const menuAnchorTenantE1 = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const getAddConnectionFeatureValue = async () => {
      try {
        const body = {
          companyKey: info.companyKey,
          featureFlagKey: 'ip-2-5308-support-custom-connection-creation',
          defaultValue: false
        };
        const res = await postRequest(
          '/integration-settings-api/feature-flag',
          body,
          false
        );
        if (res.data) {
          setAddConnectionFeature(res.data.featureFlagValue);
        }
      } catch (error: any) {
        if (error.response) {
          setAddConnectionFeature(false);
        }
      }
    };
    if (info.companyKey) {
      getAddConnectionFeatureValue();
    }
  }, [info.companyKey]);

  const handleConnectClick = useCallback(
    async (environment: string) => {
      try {
        const body = {
          connectorName: info.shortName,
          companyKey: info.companyKey,
          environment: environment,
          isPsaProduct: info.isPsaProduct,
          hostName: '',
          isCustomConnection: isCustomConnection
        };
        const res = await postRequest(
          '/integration-settings-api/install-connector',
          body,
          false
        );
        if (res.data) {
          OauthPopupLoginAndRedirect(res.data, info.name);
        }
      } catch (error: any) {
        if (error.response) {
          setShowErrorPage(true);
        }
      }
    },
    [
      isCustomConnection,
      info.companyKey,
      info.isPsaProduct,
      info.name,
      info.shortName
    ]
  );

  const handleConnectorDialogOpen = useCallback((name: string) => {
    setJiraDialog((prev: any) => ({
      ...prev,
      jira: {
        name: name,
        open: true
      }
    }));
  }, []);

  const openInstallDialog = useCallback(() => {
    setInstallDialogOpen((prev) => ({
      ...prev,
      [oauthDialogConnectors.includes(info.shortName as string)
        ? 'oauthdialog'
        : (info.shortName as string)]: true
    }));
  }, [info.shortName]);

  const handleModifyClick = useCallback(() => {
    if (info.shortName == 'replicon') {
      setOperation(ConnectorActions.MODIFY);
      openInstallDialog();
    }
  }, [openInstallDialog, info.shortName]);

  const menuItem = useCallback(
    (isCustom: boolean) => {
      if (isInstalled && !isCustom) {
        return [
          {
            name: menuPopperMessages['connectors.uninstall'],
            onClick: () => {
              handleDialogOpen();
            },
            testId: `uninstall-button-${info.name}`
          },
          {
            name: MenuItemNames.Modify,
            onClick: () => {
              handleModifyClick();
            },
            testId: `modify-button-${info.name}`
          }
        ];
      }
      if (!isInstalled || isCustom) {
        const oauthArray: {
          name: string;
          onClick: () => void;
          testId: string;
        }[] = [];
        if (info.isOauth2 === true && info.basicAuth === true) {
          oauthArray.push(
            {
              name: MenuItemNames.OAuth2,
              onClick: () => {
                handleConnectorDialogOpen(MenuItemNames.OAuth2);
              },
              testId: `Oauth2-button-${info.name}`
            },
            {
              name: MenuItemNames.BasicAuth,
              onClick: () => {
                handleConnectorDialogOpen(MenuItemNames.BasicAuth);
              },
              testId: `Basic-button-${info.name}`
            }
          );
          return oauthArray;
        } else {
          oauthArray.push(
            {
              name: MenuItemNames.Production,
              onClick: () => {
                handleConnectClick('production');
              },
              testId: `production-button-${info.name}`
            },
            {
              name: MenuItemNames.Sandbox,
              onClick: () => {
                handleConnectClick('sandbox');
              },
              testId: `sandbox-button-${info.name}`
            }
          );
          return oauthArray;
        }
      }
      return [];
    },
    [
      isInstalled,
      menuPopperMessages,
      info.name,
      info.isOauth2,
      info.basicAuth,
      handleDialogOpen,
      handleModifyClick,
      handleConnectorDialogOpen,
      handleConnectClick
    ]
  );

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleButtonClose = () => {
    setAnchorEl(null);
  };
  const classes = useStyles();

  const handleInstallDialogClose = useCallback(() => {
    setInstallDialogOpen((prev: any) => ({
      ...prev,
      [oauthDialogConnectors.includes(info.shortName)
        ? 'oauthdialog'
        : info.shortName]: false
    }));
  }, [info.shortName]);

  const handleConnectorDialogClose = useCallback(() => {
    setJiraDialog((prev: any) => ({
      ...prev,
      jira: {
        open: false
      }
    }));
  }, []);

  const handleRequestDialogClose = () => {
    setRequestDialog(false);
  };

  const isSpecificButton = useMemo(() => {
    return (
      !isInstalled &&
      info.shortName &&
      Object.values(ConnectorShortNames).includes(
        info.shortName as ConnectorShortNames
      )
    );
  }, [info.shortName, isInstalled]);

  const buttonTextIcon = useMemo(() => {
    if (isInstalled) {
      return <FormattedMessage id="connectors.uninstall" />;
    }
    if (NOT_USED_CONNECTORS.connectors.includes(info.shortName))
      return (
        <FormattedMessage id="notImplementedConnectors.requestConnectors" />
      );
    switch (info.shortName) {
      case ConnectorShortNames.Quickbooks:
        return <QbOnlineButton />;
      default:
        return <FormattedMessage id="connectors.install" />;
    }
  }, [info.shortName, isInstalled]);

  const handleClick = useCallback(
    (event, isCustom) => {
      if (isInstalled && !isCustom) {
        return info.shortName == 'replicon'
          ? handleButtonClick(event)
          : menuItem(isCustom)[0].onClick();
      }
      if (NOT_USED_CONNECTORS.connectors.includes(info.shortName as string)) {
        setRequestDialog(true);
      }
      if (info.isOauth2 === true) {
        oauthDialogConnectors.includes(info.shortName as string)
          ? openInstallDialog()
          : handleButtonClick(event);
      } else {
        dialogConnectors.includes(info.shortName as string)
          ? openInstallDialog()
          : null;
      }
    },
    [isInstalled, info.shortName, info.isOauth2, menuItem, openInstallDialog]
  );

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <div
      className={classes.infoView}
      data-testid="render-all-side-panel-details"
    >
      <div className={classes.imageContainer}>
        <div className={classes.imageConnectedDiv}>
          <img
            className={classes.connectorIcon}
            src={info.image.src}
            data-testid="connectorImgIcon"
          />
          {isInstalled && (
            <Typography
              className={classes.connected}
              variant="caption"
              data-testid={'connected-button-' + info.name}
            >
              <FormattedMessage id="connectors.installed" />
            </Typography>
          )}
        </div>
        {(genericKeyValues?.repliconConnectorConfigureAccess ||
          info?.shortName != 'replicon') && (
          <Button
            variant={isSpecificButton ? undefined : 'contained'}
            className={
              isSpecificButton
                ? classes.connectorInstallButton
                : classes.defaultInstallButton
            }
            onClick={(event) => {
              setIsCustomConnection(false);
              handleClick(event, false);
            }}
            data-testid="connectorDetailButton"
          >
            {buttonTextIcon}
          </Button>
        )}
        {info.shortName == 'xero' && addConnectionFeature && (
          <Button
            variant="contained"
            className={classes.defaultInstallButton}
            onClick={(event) => {
              setIsCustomConnection(true);
              handleClick(event, true);
            }}
            data-testid="addConnectionButton"
          >
            <FormattedMessage id="connectors.addConnection" />
          </Button>
        )}
        <RequestConnectionDialog
          companyKey={info.companyKey}
          shortName={info.shortName}
          open={requestDialog}
          handleRequestDialogClose={handleRequestDialogClose}
        />
        <RepliconConnectorInstallPage
          info={info}
          open={installdialogOpen.replicon}
          action={operation}
          handleInstallDialogClose={handleInstallDialogClose}
        />
        <BamboohrInstallPage
          info={info}
          open={installdialogOpen.bamboohr}
          handleInstallDialogClose={handleInstallDialogClose}
        />
        <ComputerEaseInstallPage
          info={info}
          open={installdialogOpen.computerease}
          handleInstallDialogClose={handleInstallDialogClose}
        />
        <ZendeskInstallDialogPage
          info={info}
          open={installdialogOpen.oauthdialog}
          handleInstallDialogClose={handleInstallDialogClose}
          setIsSignInAlertOpen={setIsSignInAlertOpen}
        />
        <ToastSnackBar
          show={isSignInAlertOpen}
          category={'info'}
          message={'Please reload after signing in.'}
          onClose={() => setIsSignInAlertOpen(false)}
          duration={null}
        />
        <JiraInstallDialogPage
          info={info}
          open={jiraDialog.jira.open}
          installName={jiraDialog.jira.name}
          handleInstallDialogClose={handleConnectorDialogClose}
        />
        <MenuPopper
          menuItem={menuItem(isCustomConnection)}
          anchorPopper={menuAnchorTenantE1}
          anchorMenu={anchorEl}
          handleButtonClose={handleButtonClose}
          isInstalled={isInstalled}
        />
        <Divider
          orientation="horizontal"
          className={classes.divider}
          data-testid="connectorImgDivider"
        ></Divider>
      </div>
      <div className={classes.textDiv}>
        <Typography className={classes.heading} variant="body2">
          <FormattedMessage id="connectors.category" />
        </Typography>
        <Typography variant="body2" data-testid="connectorCategoryText">
          <FormattedMessage id={info.category} />
        </Typography>
        <br />
        <div>
          <Typography
            className={classes.heading}
            data-testid="connectorAvailabilityText"
            variant="body2"
          >
            <FormattedMessage id="connectors.availability" />
          </Typography>
          {(info.appScope || []).map((i) => (
            <Typography
              key={i}
              className={classNames(classes.scope, classes.nextline)}
              data-testid={`connectorAvailabilityContent${i}`}
              variant="body2"
            >
              <FormattedMessage id={`connectors.appScope.${i}`} />
            </Typography>
          ))}
        </div>
      </div>
      <Divider
        orientation="horizontal"
        className={classNames(classes.divider, classes.marginBottom)}
        data-testid="connectorImgDividerInstall"
      ></Divider>
    </div>
  );
};
